/**
 * milestones.ts — Hisni milestone detection + overlay renderer.
 *
 * Ported from Apps/Memoriz/frontend/src/utils/milestones.ts +
 * Apps/Memoriz/frontend/src/components/MilestoneOverlay.ts.
 * All Memoriz-specific import paths removed; standalone in AWENI bundle.
 *
 * Motion spec E: respects prefers-reduced-motion — if reduced, the overlay
 * appears instantly with no scale/opacity animation. Full-motion uses a
 * short scale+fade entrance.
 */

export interface MilestoneEvent {
  type: 'streak' | 'total';
  value: number;
  /** i18n-ready title (English fallback) */
  title: string;
  /** i18n-ready body (English fallback) */
  body: string;
  emoji: string;
}

// Streak milestones (days)
const STREAK_MILESTONES = [7, 14, 30, 60, 100, 365];
// Total-cards milestones
const TOTAL_MILESTONES  = [10, 50, 100, 500, 1000];

/**
 * Returns a `MilestoneEvent` if the given values cross a milestone threshold,
 * otherwise `null`.  Callers should check localStorage to avoid re-showing.
 */
export function checkMilestone(
  streak: number,
  totalReviewed: number,
): MilestoneEvent | null {
  // Iterate in reverse so we find the highest crossed threshold first.
  for (let i = STREAK_MILESTONES.length - 1; i >= 0; i--) {
    const ms = STREAK_MILESTONES[i];
    if (streak >= ms) {
      return {
        type: 'streak',
        value: ms,
        title: `${ms}-Day Streak 🔥`,
        body: `You've kept your memorization going for ${ms} days in a row. Keep it up!`,
        emoji: '🔥',
      };
    }
  }
  for (let i = TOTAL_MILESTONES.length - 1; i >= 0; i--) {
    const ms = TOTAL_MILESTONES[i];
    if (totalReviewed >= ms) {
      return {
        type: 'total',
        value: ms,
        title: `${ms} Cards Reviewed ✨`,
        body: `You've reviewed ${ms} cards. Your Quran memorization is building strong foundations.`,
        emoji: '✨',
      };
    }
  }
  return null;
}

// ─── MilestoneOverlay ────────────────────────────────────────────────────────

let _overlayStylesInjected = false;

function injectOverlayStyles(): void {
  if (_overlayStylesInjected || document.getElementById('hisni-milestone-styles')) return;
  _overlayStylesInjected = true;
  const s = document.createElement('style');
  s.id = 'hisni-milestone-styles';
  s.textContent = `
    .hisni-milestone-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,.55);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      padding: var(--ln-space-4, 16px);
    }
    .hisni-milestone-card {
      background: var(--ln-bg-card, #fff);
      border-radius: var(--ln-radius-lg, 16px);
      padding: 28px 24px;
      max-width: 340px;
      width: 100%;
      text-align: center;
      box-shadow: 0 8px 32px rgba(0,0,0,.22);
      transform: scale(1);
      opacity: 1;
      transition: transform var(--motion-duration-normal, 300ms) var(--motion-easing-standard, ease-out),
                  opacity  var(--motion-duration-normal, 300ms) ease-out;
    }
    .hisni-milestone-card--enter {
      transform: scale(.8);
      opacity: 0;
    }
    .hisni-milestone-emoji { font-size: 3rem; line-height: 1; margin-bottom: 12px; }
    .hisni-milestone-title {
      font-size: 1.2rem;
      font-weight: 800;
      color: var(--ln-text-primary, #111);
      margin-bottom: 8px;
    }
    .hisni-milestone-body {
      font-size: .9rem;
      color: var(--ln-text-secondary, #666);
      line-height: 1.5;
      margin-bottom: 20px;
    }
    .hisni-milestone-cta {
      display: block;
      width: 100%;
      padding: 10px 16px;
      background: var(--ln-accent, #0A0A0A);
      color: var(--ln-accent-text, #fff);
      border: none;
      border-radius: var(--ln-radius-md, 10px);
      font-size: .95rem;
      font-weight: 700;
      cursor: pointer;
    }
    .hisni-milestone-cta:focus-visible {
      outline: 2px solid var(--ln-accent, #0A0A0A);
      outline-offset: 2px;
    }
    @media (prefers-reduced-motion: reduce) {
      .hisni-milestone-card,
      .hisni-milestone-card--enter {
        transition: none;
        transform: scale(1);
        opacity: 1;
      }
    }
  `;
  document.head.appendChild(s);
}

/**
 * Render a modal overlay celebrating a milestone.
 * Dismisses on CTA click or backdrop click.
 * Respects prefers-reduced-motion per Motion spec E.
 */
export function showMilestoneOverlay(
  event: MilestoneEvent,
  ctaLabel = 'Continue',
  onDismiss?: () => void,
): void {
  injectOverlayStyles();

  const backdrop = document.createElement('div');
  backdrop.className = 'hisni-milestone-backdrop';
  backdrop.setAttribute('role', 'dialog');
  backdrop.setAttribute('aria-modal', 'true');
  backdrop.setAttribute('aria-label', event.title);

  const card = document.createElement('div');
  card.className = 'hisni-milestone-card hisni-milestone-card--enter';

  card.innerHTML = `
    <div class="hisni-milestone-emoji" aria-hidden="true">${event.emoji}</div>
    <div class="hisni-milestone-title">${escHtml(event.title)}</div>
    <div class="hisni-milestone-body">${escHtml(event.body)}</div>
    <button class="hisni-milestone-cta">${escHtml(ctaLabel)}</button>
  `;

  backdrop.appendChild(card);
  document.body.appendChild(backdrop);

  // Trigger entrance animation (RAF ensures the browser has painted --enter state first)
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      card.classList.remove('hisni-milestone-card--enter');
    });
  });

  const dismiss = (): void => {
    backdrop.remove();
    onDismiss?.();
  };

  card.querySelector('.hisni-milestone-cta')?.addEventListener('click', dismiss);
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) dismiss();
  });

  // Trap focus on CTA
  (card.querySelector('.hisni-milestone-cta') as HTMLButtonElement | null)?.focus();
}

function escHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
